import React, { useState, useEffect , useMemo} from "react";
import {
  Dropdown,
  Option,
  Button,
  Spinner,
  Text,
  Card,
} from "@fluentui/react-components";
import { Add20Regular, Delete20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import "../CSS/WorkflowTriggersComponent.css";

export default function WorkflowTriggersComponent({
  callFlowData,
  setCallFlowData,
  permissions,
  isEditMode,
  ACTIVE_TENANT,
  workflows: allWorkflows
}) {
  const { t } = useTranslation();

  // Nombre maximum de workflows sélectionnables par callFlow.
  const MAX_WORKFLOWS = 2;

  const [workflowTriggers, setWorkflowTriggers] = useState({});
  console.log('WorkflowTriggersComponent - allWorkflows:', allWorkflows);
  
  // Convertir l'objet workflows en array et filtrer par scope du callFlow
  const workflows = useMemo(() => {
    if (!allWorkflows) return [];
    
    let workflowArray = [];
    if (Array.isArray(allWorkflows)) {
      workflowArray = allWorkflows;
    } else {
      // Si c'est un objet, convertir en array
      workflowArray = Object.values(allWorkflows);
    }
    
    // Filtrer par scope du callFlow
    const callFlowScope = callFlowData?.scope;
    if (!callFlowScope) return workflowArray;
    
    return workflowArray.filter(workflow => workflow.scope === callFlowScope);
  }, [allWorkflows, callFlowData?.scope]);

  // Initialiser workflowTriggers depuis callFlowData
  useEffect(() => {
    if (callFlowData.workflows && typeof callFlowData.workflows === 'object') {
      // Si c'est déjà le nouveau format {triggerEvent: [workflowIds]}
      if (!Array.isArray(callFlowData.workflows)) {
        setWorkflowTriggers(callFlowData.workflows);
      } else {
        // Ancien format - convertir
        const converted = {};
        callFlowData.workflows.forEach(trigger => {
          if (trigger.triggerEvent && trigger.workflowsId) {
            const workflowIds = Array.isArray(trigger.workflowsId) ? trigger.workflowsId : [trigger.workflowsId];
            converted[trigger.triggerEvent] = workflowIds;
          }
        });
        setWorkflowTriggers(converted);
      }
    } else {
      setWorkflowTriggers({});
    }
  }, [callFlowData.workflows]);

  // Nettoyer les workflows sélectionnés qui n'existent plus
  useEffect(() => {
    if (!workflows || workflows.length === 0) return;
    if (Object.keys(workflowTriggers).length === 0) return;
    
    const existingWorkflowIds = new Set(workflows.map(w => w.id));
    let hasChanges = false;
    const cleanedTriggers = {};
    
    for (const [triggerEvent, workflowIds] of Object.entries(workflowTriggers)) {
      if (Array.isArray(workflowIds)) {
        const validIds = workflowIds.filter(id => {
          const exists = existingWorkflowIds.has(id);
          if (!exists) {
            console.log(`🗑️ Workflow ${id} no longer exists, removing from ${triggerEvent}`);
          }
          return exists;
        });
        
        if (validIds.length !== workflowIds.length) {
          hasChanges = true;
        }
        if (validIds.length > 0) {
          cleanedTriggers[triggerEvent] = validIds;
        }
      }
    }
    
    if (hasChanges) {
      console.log('🧹 Cleaning up deleted workflows from selection:', cleanedTriggers);
      setWorkflowTriggers(cleanedTriggers);
      setCallFlowData(prev => ({
        ...prev,
        workflows: cleanedTriggers
      }));
    }
  }, [workflows]);

  // Mettre à jour callFlowData quand workflowTriggers change
  const updateWorkflowTriggers = (triggers) => {
    setWorkflowTriggers(triggers);
    setCallFlowData({
      ...callFlowData,
      workflows: triggers
    });
  };

  const handleWorkflowsChange = (selectedOptions) => {
    console.log('handleWorkflowsChange - selectedOptions:', selectedOptions);

    // Limite : au plus MAX_WORKFLOWS workflows par callFlow. Au-delà, on ignore
    // l'ajout (la désélection reste toujours possible car elle réduit la liste).
    if (selectedOptions.length > MAX_WORKFLOWS) {
      return;
    }

    // Grouper les workflows sélectionnés par triggerEvent
    const groupedByTrigger = {};
    selectedOptions.forEach(workflowId => {
      const workflow = workflows.find(w => w.id === workflowId);
      if (workflow && workflow.triggerEvent) {
        if (!groupedByTrigger[workflow.triggerEvent]) {
          groupedByTrigger[workflow.triggerEvent] = [];
        }
        groupedByTrigger[workflow.triggerEvent].push(workflowId);
      }
    });
    
    console.log('handleWorkflowsChange - groupedByTrigger:', groupedByTrigger);
    updateWorkflowTriggers(groupedByTrigger);
  };

  // Obtenir tous les IDs sélectionnés (tous les triggerEvents)
  const allSelectedIds = Object.values(workflowTriggers).flat();

  const canModify = permissions?.workflow?.canUpdate || permissions?.workflow?.canCreate;

  return (
    <div className="workflow-triggers-container">
      <div className="workflow-trigger-card">
        <div className="workflow-trigger-field">
          <Text weight="semibold">{t("WORKFLOWS")}</Text>
          <Text size={200} style={{ color: '#605e5c', fontStyle: 'italic', display: 'block', marginBottom: 4 }}>
            {t("WORKFLOWS_MAX_HINT", { defaultValue: `Max ${MAX_WORKFLOWS} workflows`, count: MAX_WORKFLOWS })}
          </Text>
          <Dropdown
            className="workflow-trigger-dropdown-style"
            placeholder={t("SELECT_WORKFLOWS")}
            multiselect
            value={
              allSelectedIds
                .filter(id => workflows.find(w => w.id === id))
                .map(id => {
                  const wf = workflows.find(w => w.id === id);
                  return wf.displayName || wf.id;
                })
                .join(", ")
            }
            selectedOptions={allSelectedIds}
            onOptionSelect={(e, data) => {
              handleWorkflowsChange(data.selectedOptions);
            }}
            disabled={!canModify}
          >
            {workflows.map((workflow) => (
              <Option
                key={workflow.id}
                value={workflow.id}
                disabled={allSelectedIds.length >= MAX_WORKFLOWS && !allSelectedIds.includes(workflow.id)}
              >
                {workflow.displayName} <Text size={200} style={{color: '#605e5c', fontStyle: 'italic'}}>({workflow.triggerEvent})</Text>
              </Option>
            ))}
          </Dropdown>
        </div>
      </div>
    </div>
  );
}
